/**
 * Account/auth client — OAuth2 (RFC 6749) against OUR OWN backend, not a third-party auth SaaS.
 *
 *   POST /oauth/token        — grant_type=password (sign-in) or grant_type=refresh_token (silent
 *                               refresh). Returns { access_token, refresh_token, expires_in }.
 *   POST /rest/auth/*        — sign-up, verify-code, resend, password-reset request/confirm, logout.
 *
 * The refresh token is the only thing persisted (in localStorage — it's opaque and single-purpose);
 * the access token lives in memory only and is silently refreshed shortly before it expires.
 *
 * Every request that could end with a session names two things about where it came from — this app's
 * client_id (see OAUTH_CLIENT_ID) and this browser (see deviceIdentifier) — and the server binds the
 * session to both, refusing any later refresh that does not present the same pair. So a refresh token
 * taken from this browser is of no use to another client, or from another browser.
 */
import { apiBaseUrl } from './apiBase.ts';

// Deliberately not a recognizable name (no "token"/"auth"/"awise") and the value is obfuscated at
// rest (see obfuscate/deobfuscate below) — raises the bar for casual inspection of localStorage.
// NOTE: this is NOT a defense against XSS/script-execution in the page; a script running on the
// page can read every localStorage key and reverse a string as easily as reading it plain. The
// real protections are server-side: short-lived refresh tokens, single-use rotation, reuse/theft
// detection, and the client binding below (see oauthRoutes.ts).
const REFRESH_TOKEN_KEY = 'kx7Q2vLmN9pRzT4';

/**
 * The "don't ask me for a code on this browser for 30 days" token.
 *
 * Same treatment as the refresh token, and for the same reasons: an unrecognisable key, obfuscated
 * at rest, and no illusion that either stops a script running on the page. What actually limits the
 * damage is on the server — it expires, it is stored only as a hash, and revoking a device kills it
 * everywhere at once (see mfaRepo.ts).
 *
 * It is deliberately NOT cleared on sign-out. Signing out means "I have finished", not "this
 * computer is no longer mine" — clearing it would ask for a code every single time anyone signs
 * out and back in, which is most of the point of having it gone.
 */
const DEVICE_TOKEN_KEY = 'dv8H3wYcB2kFqX6';

/**
 * This app's OAuth client_id — who we are when we ask for a token.
 *
 * A random, opaque value, fixed for the life of this client. The server records it against every
 * session this app opens and will only refresh such a session for a caller presenting the same value
 * (server/src/oauth/clientBinding.ts). So a refresh token lifted out of this browser cannot be spent
 * from a script, a CLI, or any other client that talks to the same API — it gets invalid_grant, not a
 * working session.
 *
 * IT IS NOT A SECRET, and nothing may ever be granted on the strength of it. It is compiled into the
 * JavaScript bundle and anyone can read it out of devtools or send it by hand; RFC 6749 §2.2 is
 * explicit that a client_id is not a credential. What it does is narrow and still worth having: it
 * keeps a token from crossing between clients, which rotation on its own does nothing about.
 *
 * DO NOT CHANGE IT. Every live session is bound to this exact string, so a new value would refuse to
 * refresh any of them and sign every signed-in user out on deploy. (Hardcoded rather than read from
 * REACT_APP_* for the same reason: an environment that forgot to set it would do precisely that.)
 */
const OAUTH_CLIENT_ID = 'acw-web-Q3eViougX2sngB1FpXv1HA';

/**
 * This browser, as a string — sent with every request that could end with a session, and checked on
 * every refresh thereafter (see server/src/oauth/deviceBinding.ts).
 *
 * WHAT IT BUYS. The server records it against the session and will only refresh that session for a
 * caller presenting the same value. So a refresh token lifted out of this browser — out of
 * localStorage by a script, out of a disk image, out of a copied profile — cannot be spent from
 * anywhere else: not from curl, not from another machine, not from a different locale. The client_id
 * above stops a token crossing between APPLICATIONS; this stops it crossing between BROWSERS while
 * still claiming to be this one, which is the shape an actual token theft takes.
 *
 * IT IS NOT A SECRET, and nothing is granted on the strength of it. The browser already sends its
 * user agent on every request, anybody can set the header to anything, and a script able to steal the
 * refresh token can read `navigator` just as easily. It raises the cost of replaying a token
 * elsewhere from zero to "also copy the fingerprint", and it makes a token pasted into a terminal
 * fail by default. The server stores only a hash of it and no endpoint ever returns it.
 *
 * WHY userAgent + language AND NOT SOMETHING STABLER. This is the owner's specification: the same
 * browser, with the same language. Both halves move — the user agent on roughly every browser update,
 * the language when somebody changes a setting — and when either does, that session's next refresh is
 * refused and the person signs in again. That is the accepted trade, not a defect; it is worth
 * knowing that a monthly browser release is inside the 30-day life of a refresh token, so this will
 * cost occasional, legitimate re-logins.
 *
 * WRAPPED, because this runs during module use in environments that may have no `navigator` at all
 * (a test, a server-side render). An identifier we cannot build is simply omitted — which the server
 * reads as "this session makes no claim", exactly as it did before this existed, rather than failing
 * a sign-in over a fingerprint.
 */
function deviceIdentifier(): string | undefined {
  try {
    const { userAgent, language } = navigator;
    if (!userAgent) return undefined;
    return `${userAgent} ${language ?? ''}`.trim();
  } catch {
    return undefined;
  }
}

/**
 * The two bindings every session-producing request carries. One helper so a new call site cannot pick
 * up the client_id and silently forget the device — which would leave that session unbound, and
 * unbound sessions are precisely what the check is for.
 */
function sessionBindings(): { client_id: string; device_identifier?: string } {
  const device = deviceIdentifier();
  return { client_id: OAUTH_CLIENT_ID, ...(device ? { device_identifier: device } : {}) };
}

/**
 * Shape of the JSON body returned by /oauth/token (success or error, per RFC 6749 §5.1/§5.2) and
 * by the /rest/auth/* endpoints that also hand back a fresh token pair (verify, password-reset
 * confirm). Every field is optional because which ones are present depends on which endpoint and
 * whether it succeeded.
 */
interface TokenResponseData {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
  ok?: boolean;
  exists?: boolean;
  needsEmailConfirm?: boolean;
  message?: string;
  [key: string]: unknown;
}

interface TokenRequestResult {
  ok: boolean;
  status: number;
  data: TokenResponseData;
  /** The request never reached the server — offline, server restarting, DNS, CORS. Distinct from
   *  a server that answered and said no, because the two deserve opposite responses: a refusal
   *  means the session is over, a blip means try again shortly. */
  networkError?: true;
}

/** The signed-in user (id, email, firstName, lastName). */
export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

/** Decoded (unverified) access-token JWT claims — display-only. */
interface AccessTokenClaims {
  sub?: string;
  email?: string;
  [key: string]: unknown;
}

/** Reversible, non-cryptographic obfuscation — just avoids the raw token sitting in plain sight. */
function obfuscate(value: string): string {
  return [...value].reverse().join('');
}

function deobfuscate(value: string): string {
  return [...value].reverse().join('');
}

function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

function getStoredRefreshToken(): string | null {
  const stored = localStorage.getItem(REFRESH_TOKEN_KEY);
  return stored ? deobfuscate(stored) : null;
}

function setStoredRefreshToken(token: string | null): void {
  if (token) localStorage.setItem(REFRESH_TOKEN_KEY, obfuscate(token));
  else localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/*
 * Every localStorage access is wrapped, because it THROWS rather than returning empty in a browser
 * set to block site data, and in a private window on some configurations. An unhandled throw here
 * would happen during sign-in, before any error boundary is useful — and the correct behaviour when
 * we cannot remember a device is simply to ask for a code, which is the safe answer anyway.
 */
function getStoredDeviceToken(): string | null {
  try {
    const stored = localStorage.getItem(DEVICE_TOKEN_KEY);
    return stored ? deobfuscate(stored) : null;
  } catch {
    return null;
  }
}

function setStoredDeviceToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(DEVICE_TOKEN_KEY, obfuscate(token));
    else localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {
    /* Cannot remember this browser — it will be asked for a code next time. */
  }
}

/** Forget this browser locally. Used after revoking devices, so the UI matches the server. */
export function forgetThisDevice(): void {
  setStoredDeviceToken(null);
}

// In-memory only — never persisted, so a stolen localStorage dump alone can't impersonate a live
// session for longer than the current access token's short lifetime.
let accessToken: string | null = null;
let accessTokenExpiresAt = 0; // epoch ms

function applyTokenResponse(data: TokenResponseData): void {
  accessToken = data.access_token || null;
  accessTokenExpiresAt = accessToken ? Date.now() + Math.max(0, (data.expires_in || 0) - 30) * 1000 : 0;
  setStoredRefreshToken(data.refresh_token || null);
}

function clearSession(): void {
  accessToken = null;
  accessTokenExpiresAt = 0;
  setStoredRefreshToken(null);
}

/** RFC 6749 §5.1/§5.2 token endpoint. Returns the parsed body either way (ok/error shape). */
async function requestToken(params: Record<string, string>): Promise<TokenRequestResult> {
  let res: Response;
  try {
    res = await fetch(`${getApiBaseUrl()}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      // client_id goes on BOTH grants, and it has to: the password grant is where the session
      // records it, and the refresh grant is where it is checked. Sending it on one and not the
      // other would bind every session and then refuse to refresh any of them.
      body: new URLSearchParams({ ...params, ...sessionBindings() }).toString(),
    });
  } catch {
    // `fetch` REJECTS when the request never reaches the server — a dropped connection, a server
    // restarting, a laptop waking up. This is the only place that can tell that apart from a
    // server that answered, so it is caught here rather than left to every caller: every consumer of
    // getAccessToken() but one awaited it bare, so a momentary blip during a silent token refresh
    // became an unhandled rejection and threw a full-screen error overlay over the whole app
    // (adviser-reported, 2026-08-20).
    return { ok: false, status: 0, data: {}, networkError: true };
  }
  const data: TokenResponseData = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// Refresh tokens are single-use (server rotates + revokes on reuse — see oauthRoutes.ts). If two
// callers both see the access token expired at once, they must share ONE refresh instead of each
// spending the same stored refresh token: the loser would get back an already-revoked token and
// trip the server's reuse/theft detection, which revokes the whole family (including the winner's).
let refreshPromise: Promise<string | null> | null = null;

async function doRefreshAccessToken(): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;
  const { ok, data, networkError } = await requestToken({ grant_type: 'refresh_token', refresh_token: refreshToken });
  // Unreachable is NOT a refusal. Clearing the session here would sign a client out for being
  // briefly offline — and worse, throw away a refresh token that is still perfectly good. Report
  // "no token right now" and leave the stored token alone so the next call can try again.
  if (networkError) return null;
  if (!ok) {
    clearSession();
    return null;
  }
  applyTokenResponse(data);
  return accessToken;
}

/** Silent refresh: trade the stored refresh token for a fresh pair. Clears the session on failure.
 *  Concurrent callers share the same in-flight request rather than racing separate ones. */
function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** Bearer token for API calls; null when signed out. Transparently refreshes when near expiry. */
export async function getAccessToken(): Promise<string | null> {
  if (accessToken && Date.now() < accessTokenExpiresAt) return accessToken;
  return refreshAccessToken();
}

function friendlyTokenError(data: TokenResponseData): { needsEmailConfirm: true } | { message: string } {
  if (data?.error_description === 'email_not_verified') return { needsEmailConfirm: true };
  return { message: data?.error_description || 'Incorrect email or password.' };
}

/** What sign-in can come back with, now that a password is not always the end of it. */
export type SignInResult =
  | { ok: true; mustSetUpTwoFactor: boolean }
  /** The password was right; a code from their authenticator is still needed. */
  | { ok: false; mfaRequired: true; mfaToken: string }
  | ({ ok: false } & ({ needsEmailConfirm: true } | { message: string }));

/**
 * Password sign-in (grant_type=password).
 *
 * Three ways this ends now, and the caller has to tell them apart:
 *
 *   Signed in. `mustSetUpTwoFactor` says whether they are an adviser who has not enrolled yet — they
 *   ARE signed in, but almost nothing will work until they finish, so the caller should walk them
 *   straight into setting it up rather than dropping them on a dashboard that refuses everything.
 *
 *   `mfaRequired`. The password was right and no session was issued. The `mfaToken` is good for a few
 *   minutes and buys exactly one thing: the right to present a code.
 *
 *   A refusal, as before.
 */
export async function signIn(email: string, password: string): Promise<SignInResult> {
  const deviceToken = getStoredDeviceToken();
  const { ok, data, networkError } = await requestToken({
    grant_type: 'password',
    username: email,
    password,
    // A browser that passed a challenge recently gets to skip this one.
    ...(deviceToken ? { device_token: deviceToken } : {}),
  });
  // Never blame the password for a connection problem: friendlyTokenError's default is "Incorrect
  // email or password", which is both wrong and infuriating when the server simply could not be
  // reached.
  if (networkError) {
    return { ok: false, message: "We couldn't reach AgedCareWise just then. Check your connection and try again." };
  }
  if (!ok) return { ok: false, ...friendlyTokenError(data) };

  if (data.mfa_required && typeof data.mfa_token === 'string') {
    // Deliberately NOT applied as a session — there isn't one yet.
    return { ok: false, mfaRequired: true, mfaToken: data.mfa_token };
  }

  applyTokenResponse(data);
  return { ok: true, mustSetUpTwoFactor: Boolean(data.mfa_enrolment_required) };
}

/**
 * Finish signing in with a code from the authenticator app — or with one of the recovery codes,
 * which the same field accepts, so someone without their phone does not have to find another screen.
 */
export async function completeTwoFactorChallenge(
  mfaToken: string,
  code: string,
  trustThisDevice: boolean,
): Promise<{ ok: true; usedRecoveryCode: boolean; recoveryCodesRemaining?: number } | { ok: false; message: string }> {
  let res: Response;
  try {
    res = await fetch(`${getApiBaseUrl()}/oauth/mfa/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mfa_token: mfaToken, code, trust_device: trustThisDevice, ...sessionBindings() }),
    });
  } catch {
    return { ok: false, message: "We couldn't reach AgedCareWise just then. Check your connection and try again." };
  }

  const data: TokenResponseData = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, message: String(data?.error_description || "That code wasn't right. Please try again.") };
  }

  applyTokenResponse(data);
  if (typeof data.device_token === 'string') setStoredDeviceToken(data.device_token);

  return {
    ok: true,
    usedRecoveryCode: Boolean(data.used_recovery_code),
    ...(typeof data.recoveryCodesRemaining === 'number'
      ? { recoveryCodesRemaining: data.recoveryCodesRemaining }
      : {}),
  };
}

/** Create an account. A confirmation code is always emailed (see /rest/auth/verify). */
export async function signUp(email: string, password: string): Promise<{ ok: true; needsEmailConfirm: boolean } | { ok: false; message: string }> {
  const res = await fetch(`${getApiBaseUrl()}/rest/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data: TokenResponseData = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, message: data?.message || "We couldn't create your account just now." };
  return { ok: true, needsEmailConfirm: Boolean(data.needsEmailConfirm) };
}

/** Confirm a brand-new sign-up with the 6-digit code emailed to the user. Lands the caller signed in. */
export async function verifySignupCode(email: string, code: string): Promise<{ ok: true; mustSetUpTwoFactor: boolean } | { ok: false }> {
  const res = await fetch(`${getApiBaseUrl()}/rest/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, ...sessionBindings() }),
  });
  const data: TokenResponseData = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) return { ok: false };
  applyTokenResponse(data);
  return { ok: true, mustSetUpTwoFactor: Boolean(data.mfa_enrolment_required) };
}

/** Re-send the sign-up confirmation code to the same email. */
export async function resendSignupCode(email: string): Promise<{ ok: true } | { ok: false; message?: string }> {
  const res = await fetch(`${getApiBaseUrl()}/rest/auth/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data: TokenResponseData = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, message: data?.message };
  return { ok: true };
}

/**
 * Ask the server to email a password-reset link. Returns { ok, exists } — exists=false means
 * "no account for that email" (the server checks this honestly; see routes/authRest.ts).
 */
export async function sendPasswordReset(email: string): Promise<{ ok: true; exists: boolean } | { ok: false; message: string }> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/rest/auth/password-reset/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data: { error?: { message?: string }; exists?: boolean } = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: data?.error?.message || 'Could not send the email just now.' };
    return { ok: true, exists: Boolean(data.exists) };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection and try again.' };
  }
}

/** Set a new password using the token from the emailed reset link. Lands the caller signed in. */
export async function updatePassword(resetToken: string, newPassword: string): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await fetch(`${getApiBaseUrl()}/rest/auth/password-reset/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: resetToken, password: newPassword, ...sessionBindings() }),
  });
  const data: TokenResponseData = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) return { ok: false, message: data?.message || 'That reset link is invalid or has expired.' };
  applyTokenResponse(data);
  return { ok: true };
}

/** Decode the current access token's claims without verifying it (display-only; the server is
 *  always the source of truth for anything that matters). */
function decodeAccessToken(token: string): AccessTokenClaims | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload;
  } catch {
    return null;
  }
}

/** The signed-in user (id, email, firstName, lastName), or null when signed out. */
export async function getSessionUser(): Promise<SessionUser | null> {
  const token = await getAccessToken();
  if (!token) return null;
  const claims = decodeAccessToken(token);
  if (!claims?.sub) return null;
  try {
    const res = await fetch(`${getApiBaseUrl()}/rest/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return { id: claims.sub, email: claims.email || '', firstName: '', lastName: '' };
    const data: { user: { id: string; email?: string; firstName?: string; lastName?: string } } = await res.json();
    return {
      id: data.user.id,
      email: data.user.email || '',
      firstName: data.user.firstName || '',
      lastName: data.user.lastName || '',
    };
  } catch {
    return { id: claims.sub, email: claims.email || '', firstName: '', lastName: '' };
  }
}

/**
 * Store the person's name against their LOGIN (not a free-text field on documents) — advice
 * attribution must always derive from the signed-in account, so it can't be "edited" to dodge
 * per-adviser licensing or misattribute advice.
 */
export async function setUserName(firstName: string, lastName: string): Promise<{ ok: true } | { ok: false; message?: string }> {
  const token = await getAccessToken();
  if (!token) return { ok: false, message: 'Not signed in' };
  const res = await fetch(`${getApiBaseUrl()}/rest/me/name`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
  });
  if (!res.ok) return { ok: false };
  return { ok: true };
}

export async function signOut(): Promise<void> {
  const refreshToken = getStoredRefreshToken();
  clearSession();
  if (refreshToken) {
    await fetch(`${getApiBaseUrl()}/rest/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// Two-factor authentication: setting it up, and managing it afterwards
// ---------------------------------------------------------------------------

export interface TwoFactorStatus {
  /** They have a working authenticator. */
  enrolled: boolean;
  /** They are an adviser or firm member, so they cannot decline it. */
  required: boolean;
  recoveryCodesRemaining: number;
  /** 1 = signed in with a password only; 2 = a code was presented too. */
  sessionAal: number;
}

/** Every /rest/mfa/* call goes through here so the bearer token and the empty body are never forgotten. */
async function mfaRequest<T>(
  path: string,
  init: { method?: 'GET' | 'POST' | 'DELETE'; body?: Record<string, unknown> } = {},
): Promise<{ ok: true; data: T } | { ok: false; status: number; message: string }> {
  const token = await getAccessToken();
  if (!token) return { ok: false, status: 401, message: 'Please sign in again.' };

  const method = init.method ?? 'POST';
  let res: Response;
  try {
    res = await fetch(`${getApiBaseUrl()}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      /*
       * A JSON content-type with NO body is rejected by Fastify before any handler or guard runs, so
       * an action route that takes no arguments still needs `{}` — otherwise "you are not signed in"
       * arrives dressed as a body-parsing error and nobody can tell what went wrong.
       */
      ...(method === 'GET' ? {} : { body: JSON.stringify(init.body ?? {}) }),
    });
  } catch {
    return { ok: false, status: 0, message: "We couldn't reach AgedCareWise just then. Please try again." };
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      message: String(data?.error?.message || data?.message || 'Something went wrong. Please try again.'),
    };
  }
  return { ok: true, data: data as T };
}

export async function getTwoFactorStatus(): Promise<TwoFactorStatus | null> {
  const res = await mfaRequest<TwoFactorStatus>('/rest/mfa/status', { method: 'GET' });
  return res.ok ? res.data : null;
}

/**
 * Begin setting up an authenticator. Returns the secret and the otpauth:// URI to draw as a QR code.
 * Nothing is active until confirmTwoFactorSetup succeeds — so abandoning this screen costs nothing.
 */
export async function startTwoFactorSetup(): Promise<
  { ok: true; secret: string; uri: string } | { ok: false; message: string }
> {
  const res = await mfaRequest<{ secret: string; uri: string }>('/rest/mfa/enrol/start');
  return res.ok ? { ok: true, ...res.data } : { ok: false, message: res.message };
}

/**
 * Finish setting it up. On success every other session is ended and THIS one is replaced with a
 * fresh, full-strength pair — so protecting the account does not sign the user out of it.
 *
 * The recovery codes come back exactly once and can never be retrieved again.
 */
export async function confirmTwoFactorSetup(
  code: string,
  trustThisDevice: boolean,
): Promise<{ ok: true; recoveryCodes: string[] } | { ok: false; message: string }> {
  const res = await mfaRequest<TokenResponseData & { recoveryCodes: string[] }>('/rest/mfa/enrol/confirm', {
    body: { code, trust_device: trustThisDevice, ...sessionBindings() },
  });
  if (!res.ok) return { ok: false, message: res.message };

  applyTokenResponse(res.data);
  if (typeof res.data.device_token === 'string') setStoredDeviceToken(res.data.device_token);
  return { ok: true, recoveryCodes: res.data.recoveryCodes ?? [] };
}

/**
 * Turn it off. Refused for advisers and firm staff, who may not decline it.
 *
 * The server ends every session — including this one, which is the point — and hands back a fresh
 * pair for this browser. Applying it keeps the person signed in; without that they would be quietly
 * logged out a quarter of an hour after making a change they were told had succeeded.
 */
export async function disableTwoFactor(): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await mfaRequest<TokenResponseData>('/rest/mfa/disable', { body: { ...sessionBindings() } });
  if (!res.ok) return { ok: false, message: res.message };
  if (res.data?.access_token) applyTokenResponse(res.data);
  // The 30-day pass on this browser was granted by a factor that no longer exists.
  forgetThisDevice();
  return { ok: true };
}

/** A fresh set of ten. Every existing code stops working the moment these are issued. */
export async function regenerateRecoveryCodes(): Promise<
  { ok: true; recoveryCodes: string[] } | { ok: false; message: string }
> {
  const res = await mfaRequest<{ recoveryCodes: string[] }>('/rest/mfa/recovery-codes');
  return res.ok ? { ok: true, recoveryCodes: res.data.recoveryCodes ?? [] } : { ok: false, message: res.message };
}

export interface TrustedDevice {
  id: string;
  label: string | null;
  createdAt: string;
  expiresAt: string;
}

export async function listTrustedDevices(): Promise<TrustedDevice[]> {
  const res = await mfaRequest<TrustedDevice[]>('/rest/mfa/devices', { method: 'GET' });
  return res.ok && Array.isArray(res.data) ? res.data : [];
}

/** Ask for a code again on every browser. The obvious thing to do when a device is lost or stolen. */
export async function revokeAllTrustedDevices(): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await mfaRequest('/rest/mfa/devices/revoke-all');
  // This browser is one of the ones just revoked, so drop our copy too rather than presenting a
  // token the server has already forgotten.
  if (res.ok) forgetThisDevice();
  return res.ok ? { ok: true } : { ok: false, message: res.message };
}
